
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { LogOut } from 'lucide-react';
import { DashboardSidebar } from '@/components/DashboardSidebar';
import { ChatInterface } from '@/components/ChatInterface';
import { BottomNavigation } from '@/components/BottomNavigation';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { BuyerProfileSection } from '@/components/buyer/BuyerProfileSection';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

const BuyerDashboard = () => {
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('profile');
  const [signOutLoading, setSignOutLoading] = useState(false);
  const { signOut } = useAuth();

  // Handle URL parameters for section navigation
  useEffect(() => {
    const section = searchParams.get('section');
    if (section && ['profile', 'messages'].includes(section)) {
      setActiveSection(section);
    }
  }, [searchParams]);

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setSignOutLoading(false);
    }
  };

  // Mock data
  const buyer = {
    id: 2,
    name: "Alice Smith",
    profileImage: "https://images.unsplash.com/photo-1438761681057-2025e760a8e5?w=200&h=200&fit=crop&crop=face",
    location: "California, USA",
    bio: "Avid cattle enthusiast looking for quality Angus.",
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          <div className="w-full max-w-none space-y-8">
            {/* Profile Section */}
            <BuyerProfileSection />
          </div>
        );

      case 'messages':
        return (
          <div className="w-full h-full flex flex-col min-h-0">
            <ChatInterface 
              onBack={() => setActiveSection('profile')}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ProtectedRoute requiredUserType="buyer">
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-gray-50">
          <DashboardSidebar 
            userType="buyer"
            userId={buyer.id}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            userName={buyer.name}
          />
          
          <SidebarInset className="flex-1 flex flex-col min-h-0">
            <div className={`w-full px-4 sm:px-6 lg:px-8 ${
              activeSection === 'messages' 
                ? 'flex-1 flex flex-col min-h-0 py-4 pb-20 md:pb-8' 
                : 'py-8 pb-20 md:pb-8'
            }`}>
              {activeSection !== 'messages' && (
                <div className="flex justify-between items-center mb-8">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Your Dashboard</h1>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSignOut}
                    disabled={signOutLoading}
                    className="flex items-center gap-2"
                  >
                    {signOutLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    ) : (
                      <LogOut className="w-4 h-4" />
                    )}
                    {signOutLoading ? 'Signing Out...' : 'Sign Out'}
                  </Button>
                </div>
              )}

              {renderContent()}
            </div>
          </SidebarInset>
        </div>
        
        <BottomNavigation
          userType="buyer"
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
      </SidebarProvider>
    </ProtectedRoute>
  );
};

export default BuyerDashboard;
